// @ts-nocheck
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {z} from "zod";
import {BaseButton, BaseInput, toast} from "@/components";
import useIsMobile from "@/hooks/useIsMobile";
import useRecoverPassword from "../../../hooks/useRecoverPassword";
import {useAuthStore} from "../../../store/useAuth.store";
import {useUserSupport} from "../../../store/useUserSupport.store";
import {useLogo} from "../../../util/logoUtils";

const inputCls =
  "flex items-center justify-center mx-auto w-[80%] bg-[var(--color-gray-6)] rounded-full h-12 text-base max-[426px]:w-full max-[426px]:ml-0";
const formContainerCls =
  "w-full max-w-[700px] flex flex-row justify-center gap-6 max-[920px]:flex-col max-[920px]:items-center max-[920px]:justify-center max-[920px]:gap-4";
const formInnerCls = "w-full max-w-[480px]";
const hoverTextCls = "font-[var(--font-family)] text-[var(--font-body-lg)] cursor-pointer";
const hoverLinkCls =
  "font-[var(--font-family)] text-[var(--font-body-lg)] cursor-pointer text-[var(--button-background)] font-semibold";
const hoverSupportCls = "font-[var(--font-family)] text-[var(--font-body-lg)] mt-2 p-3";

const buildSchema = t =>
  z.object({
    username: z.string().trim().min(1, t("loginUserName")),
  });

const LoginForm = () => {
  const logo = useLogo();
  const {requestPassword, isSuccess, isError, isPending} = useRecoverPassword();
  const {t} = useTranslation();
  const isTablet = useIsMobile({breakpoint: 920});
  const handleAuthForm = useAuthStore(state => state.handleAuthForm);
  const handleSupportModal = useUserSupport(state => state.handleSupportModal);

  const [username, setUsername] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isSuccess) {
      toast.success(`${t("loginPasswordRecover")} — ${t("loginPasswordRecoverMsg")}`);
    }
  }, [isSuccess, isError, t]);

  const handleSubmit = e => {
    e.preventDefault();
    const result = buildSchema(t).safeParse({username});
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? "");
      return;
    }
    setError("");
    requestPassword(result.data.username);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "auto",
      }}
    >
      <img
        src={logo}
        alt="logo"
        style={
          !isTablet
            ? {width: "var(--size-45)", height: "140px", display: "block", margin: "24px auto 24px auto"}
            : {width: "var(--size-30)", height: "var(--size-22-5)", display: "block", margin: "24px auto 16px auto"}
        }
      />
      <form
        className={formContainerCls}
        onSubmit={handleSubmit}
        autoComplete="off"
        noValidate
      >
        <div className={formInnerCls}>
          <h3 style={{fontSize: "1.5rem", display: "flex", alignItems: "center", justifyContent: "center"}}>
            {t("loginPasswordRecoverTitle")}
          </h3>

          <div style={{width: "100%", marginBottom: "var(--space-4-5)"}}>
            <BaseInput
              placeholder={t("loginEmailPlaceholder")}
              value={username}
              onChange={e => {
                setUsername(e.target.value);
                if (error) setError("");
              }}
              error={error}
              containerClassName={inputCls}
            />
          </div>

          <div style={{textAlign: "center", marginBottom: "var(--space-4-5)"}}>
            <BaseButton
              type="submit"
              className="btn-tertiary-hover"
              style={{width: "80%"}}
              loading={isPending}
            >
              {t("loginSendBtn")}
            </BaseButton>
          </div>

          <div style={{textAlign: "center"}}>
            <div style={{display: "inline-flex", alignItems: "center", gap: "var(--space-4-5)"}}>
              <button
                type="button"
                onClick={() => handleAuthForm("login")}
                className={hoverTextCls}
                style={{background: "transparent", border: "none", cursor: "pointer", padding: 0}}
              >
                {t("loginSessionTitle")}
              </button>
              <span className={hoverTextCls}>
                {t("loginNoAccount")}{" "}
                <a
                  href="/signup"
                  className={hoverLinkCls}
                >
                  {t("loginRegister")}
                </a>
              </span>
            </div>
            <p className={hoverSupportCls}>
              {t("loginRecoverEmail")}{" "}
              <button
                type="button"
                onClick={() => handleSupportModal(true)}
                className={hoverLinkCls}
                style={{background: "transparent", border: "none", cursor: "pointer", padding: 0}}
              >
                {t("loginSupport")}
              </button>
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
